import React, { useRef, useEffect } from 'react';

// Shared CRM table component to standardize header styling and structure
// across CRM pages (Contacts, Organizations, Tasks, etc.).
//
// Usage:
// <CrmTable
//   columns=[
//     { key: 'name', label: 'Name', style: { minWidth: 200 } },
//     { key: 'status', label: 'Status' },
//   ]
//   mobileCards={true}  // Converts table to cards on mobile
// >
//   {...rows with data-label attributes on tds}
// </CrmTable>
//
// By default this renders with the same table classes used on the Tasks page:
//   className="table figma-table mb-0"
// and wraps the table in a .table-responsive div.
//
// Mobile Cards: When mobileCards=true (default), the table transforms into
// card-based layout on mobile. Each row becomes a card, and each cell displays
// with its data-label as a header. Add data-label="Column Name" to each <td>.
//
// Note: stickyFirstColumns is disabled by default. The feature was causing
// layout issues (white gaps between columns) and needs to be reimplemented.

function CrmTable({
  columns,
  children,
  className = '',
  responsive = true,
  stickyFirstColumns = false,
  mobileCards = true,
  tableProps = {},
  onOverflowChange,
}) {
  const scrollContainerRef = useRef(null);
  const tableRef = useRef(null);
  const lastOverflowRef = useRef(false);

  useEffect(() => {
    if (!onOverflowChange || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;

    const checkOverflow = () => {
      let dataWidth = container.scrollWidth;

      if (!lastOverflowRef.current && tableRef.current) {
        const headers = tableRef.current.querySelectorAll('thead th');
        if (headers.length > 0) {
          dataWidth -= headers[headers.length - 1].offsetWidth;
        }
      }

      const overflowing = dataWidth > container.clientWidth;
      if (overflowing !== lastOverflowRef.current) {
        lastOverflowRef.current = overflowing;
        onOverflowChange(overflowing);
      }
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(container);
    if (tableRef.current) {
      observer.observe(tableRef.current);
    }

    return () => observer.disconnect();
  }, [onOverflowChange]);

  // Sticky lead column: reveal the right-edge shadow only once the user has
  // scrolled horizontally, and keep the 2nd column's sticky offset matched to
  // the (content-sized) checkbox column so scrolled cells can't bleed through.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!stickyFirstColumns || !container) return;

    const syncLeadOffset = () => {
      const lead = tableRef.current?.querySelector('thead th:first-child');
      if (lead && tableRef.current) {
        tableRef.current.style.setProperty('--sticky-lead-offset', `${lead.offsetWidth}px`);
      }
    };

    const onScroll = () => {
      container.classList.toggle('is-scrolled', container.scrollLeft > 0);
    };

    syncLeadOffset();
    onScroll();

    container.addEventListener('scroll', onScroll, { passive: true });
    const observer = new ResizeObserver(syncLeadOffset);
    if (tableRef.current) observer.observe(tableRef.current);

    return () => {
      container.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [stickyFirstColumns]);

  const mergedClassName = [
    'table figma-table mb-0',
    stickyFirstColumns && 'figma-table-sticky',
    mobileCards && 'responsive-card-table',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const table = (
    <table ref={tableRef} className={mergedClassName} {...tableProps}>
      <thead>
        <tr>
          {columns.map((column, index) => {
            const {
              key,
              label,
              style,
              className: thClassName,
              scope = 'col',
              ...restThProps
            } = column;

            const thKey = key || String(index);

            return (
              <th
                key={thKey}
                scope={scope}
                style={style}
                className={thClassName}
                {...restThProps}
              >
                {label}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );

  if (!responsive) return table;

  return (
    <div
      ref={scrollContainerRef}
      className="table-responsive crm-table-scroll-container"
      style={{ overflowX: 'auto', overflowY: 'visible' }}
    >
      {table}
    </div>
  );
}

export default CrmTable;
